import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown, Menu } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/Sheet';
import { BRAND_LOGO_SRC } from '@/config/brand';
import { t, type MessageKey } from '@/i18n';
import { cn } from '@/lib/utils';
import { ComingSoonBadge } from '@/components/ui/ComingSoonBadge';
import { claimFeature, isFeatureLive } from '@/config/featureAvailability';
import { getAuthToken, hasRefreshToken } from '@/services/api';
import { PRODUCT_ITEMS, USE_CASE_ITEMS, type NavItem } from '../landingNav';

type MenuId = 'product' | 'useCases';

function NavLinkItem({ item, onPick }: { item: NavItem; onPick: () => void }): JSX.Element {
  const Icon = item.icon;
  const feature = item.desc ? claimFeature(item.desc) : undefined;
  const body = (
    <>
      <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-violet-50 text-violet-700">
        <Icon className="size-4" aria-hidden="true" />
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-gray-900">{t(item.label)}</span>
        {item.desc && (
          <span className="block text-xs text-gray-500">
            {t(item.desc)}
            {feature && !isFeatureLive(feature) && <ComingSoonBadge />}
          </span>
        )}
      </span>
    </>
  );
  const cls = 'flex items-center gap-3 rounded-xl p-2.5 transition-colors hover:bg-gray-50';
  return item.href.startsWith('#') ? (
    <a href={item.href} onClick={onPick} className={cls}>{body}</a>
  ) : (
    <Link to={item.href} onClick={onPick} className={cls}>{body}</Link>
  );
}

function Dropdown({ id, label, items, open, setOpen }: {
  id: MenuId;
  label: MessageKey;
  items: NavItem[];
  open: MenuId | null;
  setOpen: (id: MenuId | null) => void;
}): JSX.Element {
  const isOpen = open === id;
  return (
    <div className="relative" onMouseEnter={() => setOpen(id)} onMouseLeave={() => setOpen(null)}>
      <button
        type="button"
        aria-expanded={isOpen}
        onClick={() => setOpen(isOpen ? null : id)}
        className="inline-flex items-center gap-1 rounded-full px-2.5 py-2 text-sm font-medium text-gray-600 transition-colors hover:text-gray-900 xl:px-3"
      >
        {t(label)}
        <ChevronDown className={cn('size-4 transition-transform', isOpen && 'rotate-180')} aria-hidden="true" />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.15 }}
            className="absolute left-0 top-full w-80 pt-2"
          >
            <div className="rounded-2xl border border-gray-100 bg-white p-2 shadow-xl">
              {items.map((item) => (
                <NavLinkItem key={item.label} item={item} onPick={() => setOpen(null)} />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/**
 * logo.png is 1024² with lockup ink ~901×436 (padding above/below). A plain
 * height cap + object-contain therefore shows a tiny wordmark. Crop to the
 * ink and size the box so the mark reads level with the menu.
 */
function NavLogo({ className }: { className?: string }): JSX.Element {
  return (
    <span
      className={cn(
        'relative block h-12 w-[100px] shrink-0 overflow-hidden sm:h-14 sm:w-[116px] md:h-16 md:w-[132px]',
        className,
      )}
    >
      <img
        src={BRAND_LOGO_SRC}
        alt={t('brand.logoAlt')}
        width={132}
        height={64}
        className="pointer-events-none absolute left-1/2 top-[45%] h-[235%] w-[235%] max-w-none -translate-x-1/2 -translate-y-1/2 object-contain"
        loading="eager"
        decoding="async"
        draggable={false}
      />
    </span>
  );
}

export function Navbar(): JSX.Element {
  // Controlled so in-page anchor taps can close the menu — an uncontrolled
  // sheet stayed open over the section the user just navigated to (QA T8.2).
  const [menuOpen, setMenuOpen] = useState(false);
  const [open, setOpen] = useState<MenuId | null>(null);
  const navRef = useRef<HTMLElement>(null);
  const closeMenu = (): void => setMenuOpen(false);
  // Token presence only — landing is public and must not wait on /auth/me.
  const isLoggedIn = Boolean(getAuthToken() || hasRefreshToken());

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') setOpen(null); };
    const onClick = (e: MouseEvent): void => {
      if (navRef.current && e.target instanceof Node && !navRef.current.contains(e.target)) setOpen(null);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, []);

  return (
    <nav ref={navRef} className="sticky top-0 z-50 border-b border-gray-100 bg-white/85 backdrop-blur-md">
      <div className="relative mx-auto flex h-16 max-w-7xl items-center px-4 sm:h-[4.5rem] md:h-20 md:px-6">
        {/* Left — logo */}
        <Link to="/" className="relative z-10 flex shrink-0 items-center" aria-label={t('landing.nav.home')}>
          <NavLogo />
        </Link>
        {/* Center — menu (true horizontal center of the bar) */}
        <div className="pointer-events-none absolute inset-x-0 hidden justify-center lg:flex">
          <div className="pointer-events-auto flex items-center gap-0.5 xl:gap-1">
            <Dropdown id="product" label="landing.nav.product" items={PRODUCT_ITEMS} open={open} setOpen={setOpen} />
            <Dropdown id="useCases" label="landing.nav.useCases" items={USE_CASE_ITEMS} open={open} setOpen={setOpen} />
            <Link to="/faq" className="rounded-full px-2.5 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 xl:px-3">
              {t('landing.nav.faq')}
            </Link>
          </div>
        </div>
        {/* Right — auth + mobile menu */}
        <div className="relative z-10 ml-auto flex items-center gap-2">
          <div className="hidden items-center gap-2 lg:flex">
            {isLoggedIn ? (
              <Link
                to="/business"
                className="rounded-full bg-gray-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-gray-800 xl:px-5 xl:py-2.5"
              >
                {t('landing.nav.backToDashboard')}
              </Link>
            ) : (
              <>
                <Link to="/login" className="rounded-full px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 xl:px-4">
                  {t('landing.nav.login')}
                </Link>
                <Link to="/signup" className="rounded-full bg-gray-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-gray-800 xl:px-5 xl:py-2.5">
                  {t('landing.nav.getStarted')}
                </Link>
              </>
            )}
          </div>
          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <button type="button" aria-label={t('landing.nav.openMenu')} className="rounded-lg p-2 transition-colors hover:bg-gray-50 lg:hidden">
                <Menu className="size-5" aria-hidden="true" />
              </button>
            </SheetTrigger>
            <SheetContent className="w-full overflow-y-auto border-gray-200 bg-white">
              <SheetHeader>
                <SheetTitle className="text-gray-900">
                  <NavLogo />
                </SheetTitle>
              </SheetHeader>
              <div className="mt-6 flex flex-col gap-1">
                <p className="px-2.5 pt-2 text-xs font-semibold uppercase tracking-wide text-gray-400">{t('landing.nav.product')}</p>
                {PRODUCT_ITEMS.map((item) => <NavLinkItem key={item.label} item={item} onPick={closeMenu} />)}
                <p className="px-2.5 pt-4 text-xs font-semibold uppercase tracking-wide text-gray-400">{t('landing.nav.useCases')}</p>
                {USE_CASE_ITEMS.map((item) => <NavLinkItem key={item.label} item={item} onPick={closeMenu} />)}
                <Link to="/faq" onClick={closeMenu} className="px-2.5 pt-4 text-sm font-semibold text-gray-900">{t('landing.nav.faq')}</Link>
                {isLoggedIn ? (
                  <Link
                    to="/business"
                    onClick={closeMenu}
                    className="mt-5 rounded-full bg-gray-900 py-3 text-center text-sm font-semibold text-white"
                  >
                    {t('landing.nav.backToDashboard')}
                  </Link>
                ) : (
                  <>
                    <Link to="/login" onClick={closeMenu} className="px-2.5 pt-3 text-sm font-semibold text-gray-900">{t('landing.nav.login')}</Link>
                    <Link to="/signup" onClick={closeMenu} className="mt-5 rounded-full bg-gray-900 py-3 text-center text-sm font-semibold text-white">
                      {t('landing.nav.getStarted')}
                    </Link>
                  </>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
}
